import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { H2, H3, Label, Description } from "@leafygreen-ui/typography";
import Toggle from "@leafygreen-ui/toggle";
import Button from "@leafygreen-ui/button";
import ConfirmationModal from "@leafygreen-ui/confirmation-modal";
import Icon from "@leafygreen-ui/icon";
import TextInput from '@leafygreen-ui/text-input';
import { Table, TableHeader, Row, Cell } from '@leafygreen-ui/table';
import IconButton from "@leafygreen-ui/icon-button";
import { css } from "@leafygreen-ui/emotion";
import Config from "../config";
import Modal from "@leafygreen-ui/modal";
import Pagination from "@leafygreen-ui/pagination";
import Banner from "@leafygreen-ui/banner";
import { useAuth0 } from "@auth0/auth0-react";
import { useApi } from "../providers/Api";
import { useSectionReorder, removeItem } from "../lib/sectionList";
import { extractVariables, applyVariables, labelFromVarName } from "../lib/templateUtils";

import { QRCode } from "react-qrcode-logo";
const QRCODE_SIZE = 480;

// --- Styles ---

let topbarStyle = css`
  text-align: right;
  margin-top: 20px;
  margin-bottom: 20px;
  .minebar {
    padding-top: 10px;
  }
  .searchbar {
    label {
      text-align: left;
    }
    padding-top: 20px;
  }
`;

let toggleButtonStyle = css`
  padding-right: 30px;
  display: block;
  float: left;
  text-align: left;
`;

const qrCodeModalStyle = css`
  text-align: center;
`

const templateListStyle = css`
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 400px;
  overflow-y: auto;
  margin-top: 16px;
`;

const templateItemStyle = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border: 1px solid #e8edeb;
  border-radius: 6px;
  cursor: pointer;
  &:hover {
    border-color: #00ed64;
    background: #f9fbf9;
  }
`;

const templateFillFormStyle = css`
  margin-top: 16px;
  > div {
    margin-bottom: 16px;
  }
`;

const templateBadgeStyle = css`
  margin-left: 8px;
  font-size: 10px;
  background: #00ed64;
  color: #023430;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: bold;
  vertical-align: middle;
  letter-spacing: 0.03em;
`;

const sectionsDividerStyle = css`
  border: none;
  border-top: 1px solid #e8edeb;
  margin: 20px 0 16px;
`;

const sectionDragItemStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid #e8edeb;
  border-radius: 6px;
  cursor: grab;
  background: white;
  user-select: none;
  margin-bottom: 6px;
  &:active {
    cursor: grabbing;
  }
`;

const sectionDragItemOverStyle = css`
  border-color: #00ed64;
  background: #f0fff4;
`;

const addSectionRowStyle = css`
  display: flex;
  gap: 8px;
  align-items: flex-end;
  margin-top: 12px;
  > div {
    flex: 1;
  }
`;

export default function Landings() {
  const { user } = useAuth0();
  let currentUserId = user.sub;
  const navigate = useNavigate();

  const { fetchLandings, insertLanding, deleteLanding, profile } = useApi();
  const isAdmin = profile?.isAdmin === true;

  const getLandings = useCallback(async () => {
    let results = await fetchLandings();
    setLandings(results);
  }, [fetchLandings]);

  useEffect(() => {
    getLandings();
  }, [getLandings]);

  const itemsPerPage = 10;
  let [paginationCurrentPage, setPaginationCurrentPage] = useState(1);
  let [paginationTotalPages, setPaginationTotalPages] = useState(1);
  let [paginationNumTotalItems, setPaginationNumTotalItems] = useState(0);
  let [currentView, setCurrentView] = useState([]);

  const refreshLandingsAfterInsert = useCallback(async () => {
    setPaginationCurrentPage(1);
    await getLandings();
  }, [getLandings]);

  let [showMyLandings, setShowMyLandings] = useState(true);
  let [searchTerm, setSearchTerm] = useState("");
  let [landings, setLandings] = useState([]);

  let [qrCodeModalOpened, setQrCodeModalOpened] = useState(false);
  let [qrCodeDestination, setQrCodeDestination] = useState("");

  // Template flow
  let [templateModalStep, setTemplateModalStep] = useState(null); // null | "pick" | "fill"
  let [selectedTemplate, setSelectedTemplate] = useState(null);
  let [templateVarValues, setTemplateVarValues] = useState({});
  let [templateIdentifier, setTemplateIdentifier] = useState("");
  let [templateIdentifierState, setTemplateIdentifierState] = useState("none");
  let [templateIdentifierError, setTemplateIdentifierError] = useState("You may only use lowercase letters, numbers, and hyphens.");
  let [fillSections, setFillSections] = useState([]);
  let [newSectionTitle, setNewSectionTitle] = useState("");
  let [newSectionSlides, setNewSectionSlides] = useState("");
  const fillReorder = useSectionReorder(fillSections, setFillSections);

  const showFilterView = () => {
    let filteredView = landings;

    if (showMyLandings) {
      filteredView = filteredView.filter((l) => {
        const isOwner = Array.isArray(l.owner)
          ? l.owner.includes(currentUserId)
          : (l.owner === currentUserId);
        const isEditor = Array.isArray(l.editors) && l.editors.includes(currentUserId);
        return isOwner || isEditor;
      });
    }

    if (searchTerm && searchTerm.length > 0) {
      filteredView = filteredView.filter(l =>
        l.identifier?.toLowerCase().includes(searchTerm) ||
        l.title?.toLowerCase().includes(searchTerm)
      );
    }

    filteredView = [...filteredView].sort((a, b) => String(b._id).localeCompare(String(a._id)));

    const numTotalItems = filteredView.length;
    setPaginationNumTotalItems(numTotalItems);
    setPaginationTotalPages(Math.ceil(numTotalItems / itemsPerPage));

    let pageIndex = paginationCurrentPage - 1;
    if (pageIndex < 0) pageIndex = 0;
    let firstItem = (pageIndex * itemsPerPage);
    let lastItem = Math.min((firstItem + itemsPerPage), numTotalItems);
    filteredView = filteredView.slice(firstItem, lastItem);
    setCurrentView(filteredView);
  };

  useEffect(() => {
    showFilterView();
  }, [paginationCurrentPage, showMyLandings, searchTerm, landings]);

  const paginate = (step) => {
    const maxPage = paginationTotalPages;
    let newPage = paginationCurrentPage + step;
    if (newPage < 1) newPage = 1;
    if (newPage > maxPage) newPage = maxPage;
    setPaginationCurrentPage(newPage);
  };

  const paginateForward = () => paginate(1);
  const paginateBackward = () => paginate(-1);

  let [deleteModalOpened, setDeleteModalOpened] = useState(false);
  let [landingToDelete, setLandingToDelete] = useState(null);

  const handleDelete = async () => {
    await deleteLanding(landingToDelete);
    await getLandings();
    setDeleteModalOpened(false);
    setLandingToDelete(null);
  }

  const showQrCode = async (route) => {
    await setQrCodeModalOpened(true);
    setQrCodeDestination(`landing.mdb.link/${route}`);
  }

  const editLanding = identifier => navigate(`/app/landings/${identifier}/edit`);
  const cloneLanding = identifier => navigate(`/app/landings/${identifier}/clone`);

  // --- Template flow handlers ---

  const resetTemplateFlow = () => {
    setTemplateModalStep(null);
    setSelectedTemplate(null);
    setTemplateVarValues({});
    setTemplateIdentifier("");
    setTemplateIdentifierState("none");
    setFillSections([]);
    setNewSectionTitle("");
    setNewSectionSlides("");
  };

  const openTemplateFlow = () => {
    resetTemplateFlow();
    setTemplateModalStep("pick");
  };

  const pickTemplate = (template) => {
    const vars = extractVariables(template);
    const initial = {};
    vars.forEach(v => { initial[v] = ""; });
    setSelectedTemplate(template);
    setTemplateVarValues(initial);
    setFillSections(template.otherSections.map(s => ({ ...s })));
    setTemplateIdentifier("");
    setTemplateIdentifierState("none");
    setTemplateModalStep("fill");
  };

  const handleTemplateIdentifierChange = (value) => {
    setTemplateIdentifier(value);
    if (!value.match(/^[a-z0-9-]+$/)) {
      setTemplateIdentifierState("error");
      setTemplateIdentifierError("You may only use lowercase letters, numbers, and hyphens.");
    } else if (landings.some(l => l.identifier === value)) {
      setTemplateIdentifierState("error");
      setTemplateIdentifierError("A landing page with this identifier already exists.");
    } else {
      setTemplateIdentifierState("valid");
    }
  };

  const createFromTemplate = async () => {
    const resolved = applyVariables({
      title: selectedTemplate.title,
      subtitle: selectedTemplate.subtitle,
      summary: selectedTemplate.summary,
      ctaButton: selectedTemplate.ctaButton,
      otherSections: fillSections,
      additionalResources: selectedTemplate.additionalResources,
    }, templateVarValues);
    resolved.identifier = templateIdentifier;
    await insertLanding(resolved);
    await refreshLandingsAfterInsert();
    resetTemplateFlow();
  };

  const removeFillSection = (index) => {
    setFillSections(removeItem(fillSections, index));
  };

  const addNewSection = () => {
    if (!newSectionTitle.trim()) return;
    const slidesLine = newSectionSlides.trim()
      ? `* [${newSectionTitle.trim()} Slides](${newSectionSlides.trim()})`
      : "";
    setFillSections([...fillSections, { title: newSectionTitle.trim(), content: slidesLine }]);
    setNewSectionTitle("");
    setNewSectionSlides("");
  };

  const templates = landings.filter(l => l.isTemplate);

  return(
    <React.Fragment>
      <H2>Landing pages</H2>
      <div className={topbarStyle}>
        <div className="minebar">
          <div className={toggleButtonStyle}>
            <Label id="toggleLabel" htmlFor="myLandingsToggle">
              Show Mine
            </Label>
            <Description>Show only landing pages that I have created</Description>
          </div>
          <div style={{display: "block", float: "left"}}>
            <Toggle
              id="myLandingsToggle"
              aria-labelledby="toggleLabel"
              onChange={(checked) => setShowMyLandings(checked)}
              checked={showMyLandings}
            />
          </div>
        </div>
        <div className="buttonbar" style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
          <Button
            onClick={openTemplateFlow}
            variant="primaryOutline"
            leftGlyph={<Icon glyph="Sparkle" />}
          >
            From Template
          </Button>
          <Button
            onClick={() => navigate("/app/landings/new")}
            variant="primary"
            leftGlyph={<Icon glyph="Plus" />}
          >
            Create New Landing Page
          </Button>
        </div>
        <div className="searchbar">
          <TextInput
            label="Search Landing Pages"
            placeholder="Search by identifier or title"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value.toLowerCase())}
          />
        </div>
      </div>

      {/* Delete modal */}
      <ConfirmationModal
        open={deleteModalOpened}
        onCancel={() => { setDeleteModalOpened(false); setLandingToDelete(null); }}
        title="Delete landing page"
        buttonText="Delete"
        variant="danger"
        onConfirm={handleDelete}
      >
        Are you sure you want to delete <strong>{landingToDelete}</strong>? This cannot be undone.
      </ConfirmationModal>

      {/* QR Code modal */}
      <Modal open={qrCodeModalOpened} setOpen={setQrCodeModalOpened} className={qrCodeModalStyle}>
        <H3>QR Code for {qrCodeDestination}</H3>
        <QRCode
            value={qrCodeDestination}
            size={QRCODE_SIZE}
            ecLevel={"Q"}
            quietZone={20}
            bgColor={"#FFFFFF"}
            fgColor={"#023430"}
            logoImage="/logo-square-dark-green.png"
            logoWidth={QRCODE_SIZE * 0.2}
            logoOpacity={0.8}
            logoPadding={1}
            logoPaddingStyle={"square"}
            removeQrCodeBehindLogo={true}
            qrStyle={"square"}
            eyeRadius={undefined}
            eyeColor={"#023430"}
          />
      </Modal>

      {/* Template flow modal */}
      <Modal open={templateModalStep !== null} setOpen={(open) => { if (!open) resetTemplateFlow(); }}>

        {templateModalStep === "pick" && (
          <>
            <H3>Select a Template</H3>
            {templates.length === 0 ? (
              <Banner variant="info" style={{ marginTop: "16px" }}>
                No templates yet.
                {isAdmin
                  ? " Mark any landing page as a template using the star icon in the list."
                  : " Ask an admin to mark a landing page as a template."}
              </Banner>
            ) : (
              <div className={templateListStyle}>
                {templates.map(t => (
                  <div key={t.identifier} className={templateItemStyle} onClick={() => pickTemplate(t)}>
                    <div>
                      <strong>{t.templateName || t.title || t.identifier}</strong>
                      <div style={{ fontSize: "12px", color: "#5c6c75", marginTop: "2px" }}>{t.identifier}</div>
                    </div>
                    <Icon glyph="ChevronRight" />
                  </div>
                ))}
              </div>
            )}
            <div style={{ marginTop: "20px", textAlign: "right" }}>
              <Button onClick={resetTemplateFlow}>Cancel</Button>
            </div>
          </>
        )}

        {templateModalStep === "fill" && selectedTemplate && (() => {
          const vars = extractVariables(selectedTemplate);
          return (
            <>
              <H3>New page from: {selectedTemplate.title || selectedTemplate.identifier}</H3>
              <form className={templateFillFormStyle}>
                <div>
                  <TextInput
                    label="Page Identifier (URL slug)"
                    description="Lowercase letters, numbers, and hyphens only."
                    value={templateIdentifier}
                    state={templateIdentifierState}
                    errorMessage={templateIdentifierError}
                    onChange={e => handleTemplateIdentifierChange(e.target.value)}
                  />
                </div>
                {vars.map(varName => (
                  <div key={varName}>
                    <TextInput
                      label={labelFromVarName(varName)}
                      value={templateVarValues[varName] || ""}
                      onChange={e => setTemplateVarValues({ ...templateVarValues, [varName]: e.target.value })}
                    />
                  </div>
                ))}
                {vars.length === 0 && (
                  <Banner variant="warning">
                    This template has no <code>{"{{variable}}"}</code> tokens. The new page will be an exact copy with a different identifier.
                  </Banner>
                )}
              </form>

              <hr className={sectionsDividerStyle} />
              <H3>Sections</H3>
              <div style={{ marginTop: "10px" }}>
                {fillSections.map((section, index) => (
                  <div
                    key={index}
                    className={`${sectionDragItemStyle}${fillReorder.dragOverIndex === index ? ` ${sectionDragItemOverStyle}` : ''}`}
                    draggable
                    onDragStart={() => fillReorder.handleDragStart(index)}
                    onDragOver={(e) => fillReorder.handleDragOver(e, index)}
                    onDrop={(e) => fillReorder.handleDrop(e, index)}
                    onDragEnd={fillReorder.handleDragEnd}
                  >
                    <Icon glyph="Drag" style={{ color: "#b8c4be", flexShrink: 0 }} />
                    <span style={{ flex: 1, fontSize: "14px" }}>{section.title}</span>
                    <IconButton aria-label="Remove section" onClick={() => removeFillSection(index)}>
                      <Icon glyph="X" fill="#aa0000" />
                    </IconButton>
                  </div>
                ))}
                {fillSections.length === 0 && (
                  <Banner variant="warning">No sections. Add one below.</Banner>
                )}
              </div>

              <div className={addSectionRowStyle}>
                <div>
                  <TextInput
                    label="Section title"
                    placeholder="e.g. MongoDB Overview"
                    value={newSectionTitle}
                    onChange={e => setNewSectionTitle(e.target.value)}
                  />
                </div>
                <div>
                  <TextInput
                    label="Slides URL"
                    placeholder="https://docs.google.com/..."
                    value={newSectionSlides}
                    onChange={e => setNewSectionSlides(e.target.value)}
                  />
                </div>
                <Button
                  variant="primaryOutline"
                  leftGlyph={<Icon glyph="Plus" />}
                  onClick={addNewSection}
                  disabled={!newSectionTitle.trim()}
                >
                  Add
                </Button>
              </div>

              <div style={{ marginTop: "20px", display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                <Button onClick={() => setTemplateModalStep("pick")}>Back</Button>
                <Button
                  variant="primary"
                  disabled={templateIdentifierState !== "valid"}
                  onClick={createFromTemplate}
                >
                  Create Landing Page
                </Button>
              </div>
            </>
          );
        })()}

      </Modal>

      <Table
        data={currentView}
        columns={[
          <TableHeader label="Identifier"  sortBy={datum => datum.identifier}/>,
          <TableHeader label="Title"  sortBy={datum => datum.title}/>,
          ...(!showMyLandings ? [<TableHeader key="owner" label="Owner" />] : []),
          <TableHeader label="Actions" />
        ]}
      >
        {({ datum }) => {
          const isOwner = Array.isArray(datum.owner) ? datum.owner.includes(currentUserId) : (datum.owner === currentUserId);
          const isEditor = Array.isArray(datum.editors) && datum.editors.includes(currentUserId);
          const canEdit = isOwner || isEditor || isAdmin;
          return (
            <Row key={datum._id}>
              <Cell>
                {datum.identifier}
                {showMyLandings && (isOwner || isEditor) && (
                  <span style={{ marginLeft: "8px", fontSize: "11px", color: "#5c6c75" }}>{isOwner ? "Owner" : "Editor"}</span>
                )}
                {datum.isTemplate && <span className={templateBadgeStyle}>TEMPLATE</span>}
              </Cell>
              <Cell>{datum.title || " "}</Cell>
              {!showMyLandings && <Cell>{datum.ownerName}</Cell>}
              <Cell>
                {(isOwner || isAdmin) &&
                <IconButton darkMode={true} aria-label="Delete" onClick={() => { setLandingToDelete(datum.identifier); setDeleteModalOpened(true); }}>
                  <Icon glyph="Trash" fill="#aa0000" />
                </IconButton>
                }
                {canEdit &&
                <IconButton darkMode={false} aria-label="Edit" onClick={() => editLanding(datum.identifier)}>
                  <Icon glyph="Edit" fill="#023430" />
                </IconButton>
                }
                <IconButton aria-label="Clone" onClick={() => cloneLanding(datum.identifier)}>
                  <Icon glyph="Clone" fill="#023430" />
                </IconButton>
                <IconButton darkMode={true} aria-label="QRCode" onClick={() => showQrCode(datum.identifier)}>
                  <Icon glyph="Sweep" />
                </IconButton>
                <IconButton aria-label="Preview" href={`${Config.LANDING.URL}/${datum.identifier}`} target="_blank">
                  <Icon glyph="Link" fill="#023430" />
                </IconButton>
              </Cell>
            </Row>
          );
        }}
      </Table>
      <div>
        <Pagination
          itemsPerPage={itemsPerPage}
          currentPage={paginationCurrentPage}
          numTotalItems={paginationNumTotalItems}
          onForwardArrowClick={() => paginateForward()}
          onBackArrowClick={() => paginateBackward()}
        />
      </div>
    </React.Fragment>
  )
}
